import { formatReceiptMoney, formatReceiptWhen, escapeReceiptHtml } from './format';
import { encodeEscPosChars, ESC_POS_CUT, ESC_POS_PROLOGUE } from './escposCharset';
import { escposQrBlock } from './escposQr';
import { thermalItemRowsHtml, wrapThermalHtml } from './renderThermalHtml';
import type { FiscalReceiptDocument, FiscalReceiptPayload } from './fiscalTypes';

const SEP = '--------------------------------';
const TEXT_WIDTH = 32;

/**
 * Marcador de posición del QR dentro de la secuencia de líneas.
 * En `text` se omite; en `escpos` emite el bloque `GS ( k`; en `html` es el `<img>`.
 */
const QR_LINE = { kind: 'qr' } as const;
type FiscalDocLine = string | null | typeof QR_LINE;

function encoder(): { bytes: number[]; push: (...vals: number[]) => void; text: (s: string) => void } {
  const bytes: number[] = [];
  return {
    bytes,
    push(...vals) {
      bytes.push(...vals);
    },
    text(s: string) {
      bytes.push(...encodeEscPosChars(s));
    },
  };
}

function trimOrNull(v?: string | null): string | null {
  const t = v?.trim();
  return t ? t : null;
}

function centered(s: string, width = TEXT_WIDTH): string {
  const pad = Math.max(0, Math.floor((width - s.length) / 2));
  return ' '.repeat(pad) + s;
}

/** `00003-00000042` — PV padStart 5, número padStart 8. */
export function formatComprobanteNumber(pointOfSale: number, invoiceNumber: number): string {
  const pv = String(Math.max(0, Math.trunc(pointOfSale))).padStart(5, '0');
  const nro = String(Math.max(0, Math.trunc(invoiceNumber))).padStart(8, '0');
  return `${pv}-${nro}`;
}

/** `XX-XXXXXXXX-X` a partir de 11 dígitos; si no son 11, devuelve el input trimmeado. */
export function formatFiscalCuit(cuit: string): string {
  const digits = cuit.replace(/\D/g, '');
  if (digits.length === 11) {
    return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`;
  }
  return cuit.trim();
}

/** dd/mm/aaaa — fecha de calendario, sin shift de timezone (inicio de actividades DATE). */
function formatFiscalDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso.trim());
  if (match) return `${match[3]}/${match[2]}/${match[1]}`;
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

type FiscalView = {
  docTypeLabel: string;
  emitterName: string;
  emitterCuit: string | null;
  emitterTaxCondition: string | null;
  emitterAddress: string | null;
  emitterIibb: string | null;
  activityStart: string | null;
  pvNro: string;
  issuedWhen: string | null;
  buyerName: string;
  buyerCuit: string | null;
  buyerTaxCondition: string | null;
  discriminatesIva: boolean;
  netAmount: string | null;
  ivaAmount: string | null;
  total: string;
  cae: string;
  caeExpiration: string | null;
  qrUrl: string | null;
  qrImageDataUrl: string | null;
  saleRef: string | null;
  copyLabel: string | null;
  diningTableName: string | null;
  relatedDocLabel: string | null;
  isHomologacion: boolean;
};

function fiscalView(payload: FiscalReceiptPayload): FiscalView {
  const net = payload.netAmount != null ? payload.netAmount : null;
  const iva = payload.ivaAmount != null ? payload.ivaAmount : null;
  const issuedWhen = formatReceiptWhen(payload.issuedAt) || null;
  return {
    docTypeLabel: payload.documentTypeLabel.trim(),
    emitterName: payload.emitterBusinessName.trim(),
    emitterCuit: trimOrNull(payload.emitterCuit)
      ? formatFiscalCuit(payload.emitterCuit)
      : null,
    emitterTaxCondition: trimOrNull(payload.emitterTaxConditionLabel),
    emitterAddress: trimOrNull(payload.emitterAddress),
    emitterIibb: trimOrNull(payload.emitterIibb),
    activityStart: formatFiscalDate(payload.emitterActivityStart) || null,
    pvNro: formatComprobanteNumber(payload.pointOfSale, payload.invoiceNumber),
    issuedWhen,
    buyerName: trimOrNull(payload.buyerName) ?? 'Consumidor Final',
    buyerCuit: trimOrNull(payload.buyerCuit) ? formatFiscalCuit(payload.buyerCuit!) : null,
    buyerTaxCondition: trimOrNull(payload.buyerTaxConditionLabel),
    discriminatesIva: net != null || iva != null,
    netAmount: net != null ? formatReceiptMoney(net) : null,
    ivaAmount: iva != null ? formatReceiptMoney(iva) : null,
    total: formatReceiptMoney(payload.total),
    cae: payload.cae.trim(),
    caeExpiration: formatFiscalDate(payload.caeExpiration) || null,
    qrUrl: trimOrNull(payload.qrUrl),
    qrImageDataUrl: trimOrNull(payload.qrImageDataUrl),
    saleRef: trimOrNull(payload.saleRef),
    diningTableName: trimOrNull(payload.diningTableName),
    copyLabel: trimOrNull(payload.copyLabel),
    relatedDocLabel: trimOrNull(payload.relatedDocLabel),
    isHomologacion: payload.environment === 'homologacion',
  };
}

function fiscalDocLines(payload: FiscalReceiptPayload, view: FiscalView): FiscalDocLine[] {
  const itemLines = payload.lines.map((line) => {
    const left = `${line.qty}x ${line.name}`;
    const right = formatReceiptMoney(line.lineTotal);
    const pad = Math.max(1, TEXT_WIDTH - left.length - right.length);
    return `${left}${' '.repeat(pad)}${right}`;
  });
  const totalPad = Math.max(1, TEXT_WIDTH - 'TOTAL'.length - view.total.length);

  return [
    centered(view.docTypeLabel),
    view.copyLabel ? centered(view.copyLabel) : null,
    centered(view.emitterName),
    view.emitterCuit ? centered(`CUIT: ${view.emitterCuit}`) : null,
    view.emitterTaxCondition ? centered(`Cond. IVA: ${view.emitterTaxCondition}`) : null,
    view.emitterAddress ? centered(`Dom.: ${view.emitterAddress}`) : null,
    view.emitterIibb ? centered(`IIBB: ${view.emitterIibb}`) : null,
    view.activityStart ? centered(`Inicio act.: ${view.activityStart}`) : null,
    SEP,
    `PV-Nro: ${view.pvNro}${view.issuedWhen ? `   Fecha: ${view.issuedWhen}` : ''}`,
    `Cliente: ${view.buyerName}`,
    view.buyerCuit ? `CUIT cliente: ${view.buyerCuit}` : null,
    view.buyerTaxCondition ? `Cond.: ${view.buyerTaxCondition}` : null,
    view.diningTableName ? `Mesa: ${view.diningTableName}` : null,
    SEP,
    ...itemLines,
    SEP,
    view.discriminatesIva && view.netAmount != null
      ? `Neto gravado ${view.netAmount}`
      : null,
    view.discriminatesIva && view.ivaAmount != null ? `IVA ${view.ivaAmount}` : null,
    !view.discriminatesIva ? 'IVA incluido en el total' : null,
    `TOTAL${' '.repeat(totalPad)}${view.total}`,
    SEP,
    view.relatedDocLabel ? centered(view.relatedDocLabel) : null,
    `CAE: ${view.cae}`,
    view.caeExpiration ? `Vto. CAE: ${view.caeExpiration}` : null,
    view.qrUrl ? QR_LINE : null,
    centered('Comprobante autorizado por AFIP/ARCA'),
    view.saleRef ? `Ref. venta: ${view.saleRef}` : null,
    view.isHomologacion
      ? '** COMPROBANTE DE PRUEBA - SIN VALIDEZ FISCAL **'
      : null,
  ];
}

/** ESC/POS del comprobante fiscal (CP850; QR nativo si viene `qrUrl`). */
export function renderFiscalComprobanteEscPos(payload: FiscalReceiptPayload): Uint8Array {
  const view = fiscalView(payload);
  const out = encoder();
  out.push(...ESC_POS_PROLOGUE);
  for (const line of fiscalDocLines(payload, view)) {
    if (line == null) continue;
    if (typeof line === 'object') {
      out.push(...escposQrBlock(view.qrUrl!));
      continue;
    }
    out.text(`${line}\n`);
  }
  out.push(...ESC_POS_CUT);
  return Uint8Array.from(out.bytes);
}

export function buildFiscalComprobante(payload: FiscalReceiptPayload): FiscalReceiptDocument {
  const widthMm = payload.widthMm ?? 72;
  const view = fiscalView(payload);
  const docLines = fiscalDocLines(payload, view);

  const text = docLines
    .filter((l): l is string => typeof l === 'string' && l !== '')
    .join('\n');

  const meta = (s: string, cls = 'meta') =>
    `<div class="${cls}">${escapeReceiptHtml(s)}</div>`;

  const emitterHtml = [
    meta(view.emitterName, 'meta center'),
    view.emitterCuit ? meta(`CUIT: ${view.emitterCuit}`, 'meta center') : '',
    view.emitterTaxCondition
      ? meta(`Cond. IVA: ${view.emitterTaxCondition}`, 'meta center')
      : '',
    view.emitterAddress ? meta(`Dom.: ${view.emitterAddress}`, 'meta center') : '',
    view.emitterIibb ? meta(`IIBB: ${view.emitterIibb}`, 'meta center') : '',
    view.activityStart ? meta(`Inicio act.: ${view.activityStart}`, 'meta center') : '',
  ].join('');

  const buyerHtml = [
    meta(`PV-Nro: ${view.pvNro}`),
    view.issuedWhen ? meta(`Fecha: ${view.issuedWhen}`) : '',
    meta(`Cliente: ${view.buyerName}`),
    view.buyerCuit ? meta(`CUIT cliente: ${view.buyerCuit}`) : '',
    view.buyerTaxCondition ? meta(`Cond.: ${view.buyerTaxCondition}`) : '',
  ].join('');

  const totalsHtml = [
    view.discriminatesIva && view.netAmount != null
      ? `<div class="row">Neto gravado ${view.netAmount}</div>`
      : '',
    view.discriminatesIva && view.ivaAmount != null
      ? `<div class="row">IVA ${view.ivaAmount}</div>`
      : '',
    !view.discriminatesIva ? '<div class="row">IVA incluido en el total</div>' : '',
    `<div class="total">TOTAL ${view.total}</div>`,
  ].join('');

  const qrHtml = view.qrImageDataUrl
    ? `<div class="center"><img src="${escapeReceiptHtml(view.qrImageDataUrl)}" alt="QR AFIP" style="display:block;width:48mm;max-width:100%;margin:6px auto;"/></div>`
    : view.qrUrl
      ? '<div class="meta center">QR no disponible</div>'
      : '';

  const body = `
  <div class="title">${escapeReceiptHtml(view.docTypeLabel)}</div>
  ${view.copyLabel ? `<div class="meta center">${escapeReceiptHtml(view.copyLabel)}</div>` : ''}
  ${emitterHtml}
  <hr class="sep"/>
  ${buyerHtml}
  <hr class="sep"/>
  <table>${thermalItemRowsHtml(payload.lines) || '<tr><td>(sin ítems)</td></tr>'}</table>
  <hr class="sep"/>
  ${totalsHtml}
  <hr class="sep"/>
  ${view.relatedDocLabel ? meta(view.relatedDocLabel, 'meta center') : ''}
  ${meta(`CAE: ${view.cae}`)}
  ${view.caeExpiration ? meta(`Vto. CAE: ${view.caeExpiration}`) : ''}
  ${qrHtml}
  <div class="foot">Comprobante autorizado por AFIP/ARCA</div>
  ${view.saleRef ? `<div class="foot">Ref. venta: ${escapeReceiptHtml(view.saleRef)}</div>` : ''}
  ${
    view.isHomologacion
      ? '<div class="void-banner">COMPROBANTE DE PRUEBA — SIN VALIDEZ FISCAL</div>'
      : ''
  }`;

  return {
    text,
    html: wrapThermalHtml(body, widthMm),
    escpos: renderFiscalComprobanteEscPos(payload),
  };
}
